import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import { visuallyHidden } from "@mui/utils";
import axios from "axios";
import Alert from "@mui/material/Alert";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "react-toastify";
import { Button, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import AddCategories from "./AddCategories.jsx";
import UpdateCategory from "./UpdateCategory.jsx";
import ENV from "../../../environment.js";
import Spinner from "react-spinner-material";

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

let headCells = [
    { id: "id", numeric: true, label: "ID" },
    { id: "name", numeric: true, label: "Name" },
    { id: "description", numeric: true, label: "Description" },
    { id: "number_of_people", numeric: true, label: "Number of people" },
    { id: "is_shared", numeric: true, label: "Shared" },
    { id: "null", numeric: true, label: "" },
    { id: "actions", numeric: true, label: "Actions" },
];

function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell key={headCell.id} align="left" sortDirection={orderBy === headCell.id ? order : false}>
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

function CategoriesTable() {
    const navigate = useNavigate();
    const { hotelid } = useParams();
    const [deleteLoading, setDeleteLoading] = React.useState(false);
    const [idx, setIdx] = React.useState("");
    const [data, setData] = React.useState([]);
    const [error, setError] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("calories");
    const [anchor, setAnchor] = React.useState(null);
    const [currencyTypes, setCurrencyTypes] = React.useState([]);

    const getData = async () => {
        await axios
            .get(`${ENV.URL}/api/hdp/tts/adm/hotel/category/${hotelid}`, {
                headers: { Authorization: sessionStorage.getItem("token"), "client-id": ENV.CLIENTID, "client-secret": ENV.SECRET },
            })
            .then((res) => {
                setLoading(false);
                setData(res.data.data);
            })
            .catch((err) => {
                setLoading(false);
                toast.error(err?.response?.data?.errors[0]?.message || "An error occurred");
                setError(true);
            });
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const openMenu = (event, elem) => {
        setIdx(elem);
        setAnchor(event.currentTarget);
    };

    const closeMenu = () => {
        setAnchor(null);
    };

    const onDelete = async (e) => {
        setDeleteLoading(true);
        await axios
            .delete(`${ENV.URL}/api/hdp/tts/adm/hotel/category/${e}/${hotelid}`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: sessionStorage.getItem("token"),
                    "client-id": ENV.CLIENTID,
                    "client-secret": ENV.SECRET,
                },
            })
            .then((res) => {
                setDeleteLoading(false);
                toast.success("Deleted successfully", { autoClose: 2000 });
                closeMenu();
                getData();
            })
            .catch((err) => {
                setDeleteLoading(false);
                toast.error(err?.response?.data?.errors[0]?.message || "An error occurred");
            });
    };

    const getActiveCurrencyTupes = async (hotelId) => {
        await axios
            .get(`${ENV.URL}/api/hdp/tts/adm/hotel/${hotelId}/currency/active`, {
                headers: { Authorization: sessionStorage.getItem("token"), "client-id": ENV.CLIENTID, "client-secret": ENV.SECRET },
            })
            .then((res) => {
                setCurrencyTypes(res.data.data);
            })
            .catch((err) => {
                toast.error(err?.response?.data?.errors[0]?.message || "An error occurred");
                setError(true);
            });
    };

    const onPicture = (id) => {
        navigate(`/hotel/${hotelid}/category/${id}/images`);
    };

    React.useEffect(() => {
        getData();
        getActiveCurrencyTupes(hotelid);
    }, []);

    return (
        <div className="container mt-3">
            {deleteLoading && <div className="overlay"></div>}
            <AddCategories getData={getData} currencyTypes={currencyTypes} />
            <h3>Categories</h3>
            {loading ? (
                <div style={{ marginTop: "100px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <Spinner radius={150} color={"#1976d2"} stroke={16} visible={true} />
                </div>
            ) : (
                <Box sx={{ width: "100%", marginTop: "20px" }}>
                    {!error && data.length === 0 && (
                        <Alert variant="outlined" severity="warning">
                            No Data yet
                        </Alert>
                    )}
                    {error && (
                        <Alert variant="outlined" severity="error">
                            Could not get data
                        </Alert>
                    )}
                    {data.length >= 1 && (
                        <Paper sx={{ width: "100%", mb: 2 }}>
                            <TableContainer>
                                <Table sx={{ minWidth: 750, px: 2 }} aria-labelledby="tableTitle" size="medium">
                                    <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} rowCount={data.length} />
                                    <TableBody>
                                        {stableSort(data, getComparator(order, orderBy)).map((row) => {
                                            return (
                                                <TableRow hover tabIndex={-1} key={row.id}>
                                                    <TableCell align="left">{row.id}</TableCell>
                                                    <TableCell align="left">{row.name}</TableCell>
                                                    <TableCell align="left">{row.description}</TableCell>
                                                    <TableCell align="left">{row.number_of_people}</TableCell>
                                                    <TableCell align="left">{row.is_shared ? "Yes" : "No"}</TableCell>
                                                    <TableCell align="left">
                                                        <Button
                                                            variant="outlined"
                                                            onClick={(e) => {
                                                                onPicture(row.id);
                                                                sessionStorage.setItem("categoryname", row.name);
                                                            }}
                                                        >
                                                            Manage
                                                        </Button>
                                                    </TableCell>
                                                    <TableCell align="left">
                                                        <Box sx={{ flexGrow: 0 }}>
                                                            <Tooltip title="Actions">
                                                                <IconButton
                                                                    style={{ borderRadius: "50%", padding: "2px" }}
                                                                    onClick={(e, elem = row) => openMenu(e, elem)}
                                                                    sx={{ p: 0 }}
                                                                >
                                                                    <MoreVertIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Menu
                                                                sx={{ mt: "23px" }}
                                                                id="menu-appbar"
                                                                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                                                                transformOrigin={{ vertical: "top", horizontal: "left" }}
                                                                anchorEl={anchor}
                                                                open={Boolean(anchor)}
                                                                onClose={closeMenu}
                                                            >
                                                                <MenuItem onClick={(e) => onDelete(idx.id)}>
                                                                    <Typography>Delete</Typography>
                                                                </MenuItem>
                                                                <MenuItem>
                                                                    <UpdateCategory category={idx} getTableData={getData} close={closeMenu} />
                                                                </MenuItem>
                                                            </Menu>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                </Box>
            )}
        </div>
    );
}

export default React.memo(CategoriesTable);
